import React, { useState, useEffect } from 'react';
import { vehicleRegistrationAPI } from '../services/api';
import './DataEntryForm.css';

const DataEntryForm = ({ businessTypes, vehicleTypes, units, user, onDataSaved }) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedUnit, setSelectedUnit] = useState((user.role === 'unit' || user.role === 'supervisor') ? (user.unit?.id || '') : '');
  const [existingData, setExistingData] = useState({});
  const [searchUnit, setSearchUnit] = useState('');
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [filteredUnits, setFilteredUnits] = useState([]);

  useEffect(() => {
    if (selectedDate && selectedUnit) {
      fetchExistingData();
    }
  }, [selectedDate, selectedUnit]);

  // Filter units for search (admin only UI usage)
  useEffect(() => {
    if (!units) return;
    if (searchUnit.trim() === '') {
      setFilteredUnits(units);
    } else {
      const filtered = units.filter(unit =>
        unit.name.toLowerCase().includes(searchUnit.toLowerCase())
      );
      setFilteredUnits(filtered);
    }
  }, [units, searchUnit]);

  // Update search text when selectedUnit changes
  useEffect(() => {
    if (selectedUnit && units && units.length > 0) {
      const selected = units.find(u => u.id === parseInt(selectedUnit));
      if (selected) {
        setSearchUnit(selected.name);
      }
    } else {
      setSearchUnit('');
    }
  }, [selectedUnit, units]);

  const handleUnitSearch = (e) => {
    setSearchUnit(e.target.value);
    setShowUnitDropdown(true);
  };

  const handleUnitSelect = (unit) => {
    setSelectedUnit(unit.id);
    setSearchUnit(unit.name);
    setShowUnitDropdown(false);
  };

  const handleUnitInputFocus = () => {
    setShowUnitDropdown(true);
  };

  const handleUnitInputBlur = () => {
    setTimeout(() => setShowUnitDropdown(false), 200);
  };

  const handleClearUnit = () => {
    setSelectedUnit('');
    setSearchUnit('');
    setShowUnitDropdown(false);
  };

  const fetchExistingData = async () => {
    try {
      const params = {
        date: selectedDate,
        unitId: (user.role === 'unit' || user.role === 'supervisor') ? (user.unit?.id || '') : selectedUnit
      };

      const response = await vehicleRegistrationAPI.getRegistrations(params);
      const existingRecords = {};

      response.data.records.forEach(record => {
        const key = `${record.businessTypeId}-${record.vehicleTypeId}`;
        existingRecords[key] = record.quantity;
      });

      setExistingData(existingRecords);
      setFormData(existingRecords);
    } catch (error) {
      console.error('Error fetching existing data:', error);
    }
  };

  const handleQuantityChange = (businessTypeId, vehicleTypeId, value) => {
    const key = `${businessTypeId}-${vehicleTypeId}`;
    // Xử lý đúng giá trị 0: nếu value rỗng thì 0, nếu không thì parseInt
    const numValue = value === '' ? 0 : parseInt(value);
    setFormData(prev => ({
      ...prev,
      [key]: isNaN(numValue) ? 0 : numValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (user.role === 'admin' && !selectedUnit) {
      setMessage('Vui lòng chọn đơn vị');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const promises = [];

      // Tạo danh sách tất cả các ô có thể có (từ cấu trúc bảng)
      const { columns } = buildTableStructure();

      for (const col of columns) {
        const key = `${col.businessType.id}-${col.vehicleType.id}`;
        // Lấy giá trị từ formData, nếu không có thì mặc định là 0
        const quantity = formData[key] !== undefined ? formData[key] : 0;

        const recordData = {
          businessTypeId: col.businessType.id,
          vehicleTypeId: col.vehicleType.id,
          recordDate: selectedDate,
          quantity: quantity,
          unitId: (user.role === 'unit' || user.role === 'supervisor') ? (user.unit?.id || '') : selectedUnit,
          notes: ''
        };

        promises.push(vehicleRegistrationAPI.upsertDailyRecord(recordData));
      }

      await Promise.all(promises);

      setMessage('Lưu dữ liệu thành công!');

      // Delay refresh để message hiển thị trước
      setTimeout(async () => {
        await fetchExistingData();

        if (onDataSaved) {
          onDataSaved();
        }
      }, 2000);
    } catch (error) {
      console.error('Error saving data:', error);
      setMessage('❌ Lỗi khi lưu dữ liệu: ' + (error.response?.data?.message || error.message));

      // Thông báo lỗi không tự động ẩn
    } finally {
      setLoading(false);
    }
  };

  // Tạo cấu trúc bảng tương tự SpreadsheetReport
  const buildTableStructure = () => {
    if (!businessTypes.length || !vehicleTypes.length) return null;

    // Tạo cấu trúc cột
    const columns = [];
    vehicleTypes.forEach(vehicleType => {
      const vehicleBusinessTypes = businessTypes.filter(bt =>
        bt.vehicleTypes && bt.vehicleTypes.some(vt => vt.id === vehicleType.id)
      );

      vehicleBusinessTypes.forEach(businessType => {
        columns.push({
          vehicleType,
          businessType,
          key: `${vehicleType.id}-${businessType.id}`
        });
      });
    });

    return { columns };
  };

  const tableStructure = buildTableStructure();

  const getTotalQuantity = () => {
    return Object.values(formData).reduce((sum, qty) => sum + (qty || 0), 0);
  };

  const getTotalByCategory = () => {
    if (!tableStructure) return {};

    const { columns } = tableStructure;
    const totals = {};

    columns.forEach(col => {
      const key = `${col.businessType.id}-${col.vehicleType.id}`;
      const quantity = formData[key] || 0;
      const category = col.vehicleType.category;

      if (!totals[category]) {
        totals[category] = 0;
      }
      totals[category] += quantity;
    });

    return totals;
  };

  if (!tableStructure) {
    return (
      <div className="data-entry-form">
        <h2>Nhập liệu đăng ký xe</h2>
        <div className="loading">Đang tải dữ liệu...</div>
      </div>
    );
  }

  const { columns } = tableStructure;

  // Nhóm cột theo loại phương tiện
  const groupedColumns = {};
  columns.forEach(col => {
    if (!groupedColumns[col.vehicleType.id]) {
      groupedColumns[col.vehicleType.id] = {
        vehicleType: col.vehicleType,
        columns: []
      };
    }
    groupedColumns[col.vehicleType.id].columns.push(col);
  });

  return (
    <div className="data-entry-form">
      <h2>Nhập liệu đăng ký xe</h2>

      {message && (
        <div className={`message ${message.includes('thành công') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label>Ngày báo cáo:</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              required
            />
          </div>

          {user.role === 'admin' && (
            <div className="form-group">
              <label>Địa điểm:</label>
              <div className="searchable-select">
                <div className="input-wrapper">
                  <input
                    type="text"
                    placeholder="Chọn địa điểm..."
                    value={searchUnit}
                    onChange={handleUnitSearch}
                    onFocus={handleUnitInputFocus}
                    onBlur={handleUnitInputBlur}
                    required
                  />
                  {searchUnit && (
                    <button
                      type="button"
                      className="clear-btn"
                      onClick={handleClearUnit}
                      title="Xóa lựa chọn"
                    >
                      ×
                    </button>
                  )}
                </div>
                {showUnitDropdown && filteredUnits.length > 0 && (
                  <div className="dropdown-list">
                    {filteredUnits.map(unit => (
                      <div
                        key={unit.id}
                        className="dropdown-item"
                        onClick={() => handleUnitSelect(unit)}
                      >
                        {unit.name}
                      </div>
                    ))}
                  </div>
                )}
                {showUnitDropdown && filteredUnits.length === 0 && searchUnit && (
                  <div className="dropdown-list">
                    <div className="dropdown-item no-results">
                      Không tìm thấy địa điểm
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="entry-table-container">
          <table className="entry-table">
            <thead>
              {/* Header cấp 1: Loại phương tiện */}
              <tr className="vehicle-header">
                {Object.values(groupedColumns).map(group => (
                  <th key={group.vehicleType.id} colSpan={group.columns.length} className="vehicle-type-header">
                    {group.vehicleType.name}
                  </th>
                ))}
              </tr>
              {/* Header cấp 2: Nghiệp vụ */}
              <tr className="business-header">
                {Object.values(groupedColumns).map(group =>
                  group.columns.map(col => (
                    <th key={col.key} className="business-type-header">
                      {col.businessType.name}
                    </th>
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              <tr className="input-row">
                {columns.map(col => {
                  const key = `${col.businessType.id}-${col.vehicleType.id}`;
                  return (
                    <td key={col.key} className="input-cell">
                      <input
                        type="number"
                        min="0"
                        value={formData[key] !== undefined ? formData[key] : 0}
                        onChange={(e) => handleQuantityChange(col.businessType.id, col.vehicleType.id, e.target.value)}
                        className="quantity-input"
                      />
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>

        <div className="total-section">
          <div className="total-breakdown">
            {Object.entries(getTotalByCategory())
              .filter(([category]) => category === 'moto' || category === 'oto')
              .map(([category, total]) => {
                const categoryName = category === 'moto' ? 'Mô tô' : 'Ô tô';
                return (
                  <div key={category} className="category-total">
                    <span className="category-name">{categoryName}:</span>
                    <span className="category-count">{total} xe</span>
                  </div>
                );
              })}
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" disabled={loading} className={loading ? 'loading' : ''}>
            {loading ? '⏳ Đang lưu...' : '💾 Lưu dữ liệu'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DataEntryForm;
